import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skull, Search, ChevronDown, Heart, Shield, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { PageLoader, PageHeader } from "@/components/common";

const CR_BUCKETS = [
  { label: "All", min: -1, max: -1 },
  { label: "0–1", min: 0, max: 1 },
  { label: "2–5", min: 2, max: 5 },
  { label: "6–10", min: 6, max: 10 },
  { label: "11–15", min: 11, max: 15 },
  { label: "16+", min: 16, max: 99 },
];

const TYPE_FILTERS = ["", "beast", "dragon", "undead", "humanoid", "giant", "fiend", "celestial", "fey", "elemental", "aberration", "construct", "plant", "ooze", "monstrosity"];

function crColor(cr) {
  if (cr == null) return "#94a3b8";
  if (cr < 1) return "#22c55e";
  if (cr < 5) return "#fbbf24";
  if (cr < 10) return "#f97316";
  if (cr < 15) return "#ef4444";
  return "#a855f7";
}

export default function Bestiary() {
  const [search, setSearch] = useState("");
  const [crBucket, setCrBucket] = useState(CR_BUCKETS[0]);
  const [type, setType] = useState("");
  const [expanded, setExpanded] = useState(null);

  const { data: monsters, isLoading } = useQuery({
    queryKey: ["bestiary", search, crBucket.min, crBucket.max, type],
    queryFn: async () =>
      (await api.get("/dnd5e/monsters", { params: { search, cr_min: crBucket.min, cr_max: crBucket.max, type } })).data,
  });

  return (
    <div>
      <PageHeader title="Bestiary" subtitle="Every creature from the SRD — searchable and ready to drop into your campaign" icon={Skull} />

      <div className="flex flex-col gap-3 mb-6">
        <div className="relative">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search monsters…" className="input-dark w-full rounded-xl pl-11 pr-4 py-2.5 text-sm" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {CR_BUCKETS.map((b) => (
            <button key={b.label} onClick={() => setCrBucket(b)} className={`px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition ${crBucket.label === b.label ? "btn-violet" : "glass glass-hover text-zinc-400"}`}>CR {b.label}</button>
          ))}
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {TYPE_FILTERS.map((t) => (
            <button key={t} onClick={() => setType(t)} className={`px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap capitalize transition ${type === t ? "btn-violet" : "glass glass-hover text-zinc-400"}`}>{t || "All types"}</button>
          ))}
        </div>
      </div>

      {isLoading ? <PageLoader /> : (
        <div className="grid md:grid-cols-2 gap-3">
          {monsters?.map((m) => {
            const cr = m.challenge_rating;
            const isOpen = expanded === m.index;
            return (
              <div key={m.index} className="glass rounded-2xl overflow-hidden">
                <button onClick={() => setExpanded(isOpen ? null : m.index)} className="w-full p-4 flex items-start gap-3 text-left">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center font-display font-bold shrink-0 text-sm" style={{ background: `${crColor(cr)}22`, color: crColor(cr) }}>
                    {cr == null ? "?" : cr < 1 && cr > 0 ? "⅛" : cr}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white">{m.name}</div>
                    <div className="text-xs text-zinc-500 capitalize">{m.size} {m.type} · {m.alignment}</div>
                  </div>
                  <ChevronDown size={16} className={`text-zinc-500 transition ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && <MonsterDetail m={m} />}
              </div>
            );
          })}
          {monsters?.length === 0 && <p className="text-zinc-600 col-span-2 text-center py-12">No creatures match.</p>}
        </div>
      )}
    </div>
  );
}

function MonsterDetail({ m }) {
  const ac = Array.isArray(m.armor_class) && m.armor_class[0]?.value;
  const speed = m.speed ? Object.entries(m.speed).map(([k, v]) => `${k} ${v}`).join(", ") : "";
  const stats = [
    ["STR", m.strength], ["DEX", m.dexterity], ["CON", m.constitution],
    ["INT", m.intelligence], ["WIS", m.wisdom], ["CHA", m.charisma],
  ];
  return (
    <div className="px-4 pb-4 space-y-3 border-t border-white/[0.06]">
      <div className="grid grid-cols-3 gap-2 pt-3 text-xs">
        <Stat icon={Shield} label="AC" value={ac} />
        <Stat icon={Heart} label="HP" value={`${m.hit_points} (${m.hit_dice})`} />
        <Stat icon={Zap} label="XP" value={m.xp} />
      </div>
      <div className="text-xs text-zinc-500"><span className="text-zinc-400 capitalize">Speed:</span> {speed}</div>
      <div className="grid grid-cols-6 gap-1.5">
        {stats.map(([l, v]) => (
          <div key={l} className="glass rounded-lg px-2 py-1.5 text-center">
            <div className="text-[10px] text-zinc-500">{l}</div>
            <div className="font-semibold text-white text-sm">{v ?? "–"}</div>
          </div>
        ))}
      </div>
      {m.special_abilities?.length > 0 && (
        <div className="space-y-1.5 pt-1">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Abilities</div>
          {m.special_abilities.map((a, i) => (
            <div key={i} className="text-xs text-zinc-300"><span className="font-semibold text-violet-300">{a.name}.</span> {a.desc}</div>
          ))}
        </div>
      )}
      {m.actions?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Actions</div>
          {m.actions.map((a, i) => (
            <div key={i} className="text-xs text-zinc-300"><span className="font-semibold text-amber-300">{a.name}.</span> {a.desc}</div>
          ))}
        </div>
      )}
      {m.legendary_actions?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs uppercase tracking-wider text-rose-400">Legendary Actions</div>
          {m.legendary_actions.map((a, i) => (
            <div key={i} className="text-xs text-zinc-300"><span className="font-semibold text-rose-300">{a.name}.</span> {a.desc}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="glass rounded-lg px-3 py-2 flex items-center gap-2">
      <Icon size={14} className="text-zinc-500" />
      <div><div className="text-[10px] text-zinc-500 uppercase">{label}</div><div className="text-sm font-semibold text-white">{value ?? "–"}</div></div>
    </div>
  );
}
